import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { BodySmReg, TitleLgReg } from '@seed-health/tokens';
import {
  mqTabletMin,
  mqXLargeMin,
  mqLargeMin,
} from 'styled/tools/media-queries';

import RawHtmlVideo from 'components/pdp/raw-html-video';
import ClinicalTrials from 'components/pdp/clinical-trials';
import Icon from 'components/global/Icon/Primary';
import DSLearnMore from 'components/pdp/ds01/ds-learn-more';
import type {
  EssentialsBundleItem,
  EssentialsModule,
} from 'lib/pdp-modules/types';

const NutrientDelivery = styled.div`
  margin: 0 1rem;
  position: relative;
  border-radius: 2rem;
  padding: 3rem 1.5rem;
  overflow: hidden;
  color: var(--fill-primary);
  transition: background 0.2s;

  @media ${mqLargeMin} {
    margin: 0;
    padding: 3rem;
    min-height: 34.25rem;

    .mini-grid & {
      grid-column: 1 / span 2;
    }

    &.is-ds01 {
      padding: 6rem 3rem;
    }

    &:not(.is-ds01) {
      display: flex;
      flex-direction: column;
    }
  }

  * {
    color: inherit;
  }
`;

const NutrientDeliveryTitle = styled.h2`
  ${TitleLgReg};
  position: relative;
  margin-bottom: 1.5rem;
  max-width: 25.5rem;
  z-index: 1;

  @media ${mqLargeMin} {
    font-size: var(--font-size-800);
  }
`;

const NutrientDeliveryContent = styled.p`
  max-width: 15.5rem;
  letter-spacing: -0.16px;
  line-height: 140%;
  z-index: 2;

  &.is-ds01 {
    border-radius: 1.5rem;
    padding: 0.75rem 1.5rem;
    background-color: var(--fill-bright);
    color: var(--fill-brand);
    font-weight: 500;
  }
`;

const DeliveryPillParts = styled.div`
  position: relative;
  transition: opacity 1s;
  margin-top: 3rem;
  width: 100%;
  max-width: 100%;
  display: block;

  > span {
    position: absolute;
    z-index: 0;
    top: 0;
    right: -1rem;
    transform: translateX(30%);
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    pointer-events: none;
  }

  > span video {
    pointer-events: auto;
  }

  video {
    position: static;
    display: block;
    height: 360px;
    width: auto;
    max-width: none;
    object-fit: cover;
    object-position: center;
    transform: none;
  }

  img {
    position: absolute;
    z-index: 0;
    left: calc(100% - 1.8125rem);
    right: auto;
    max-width: 3.625rem;
    transition:
      top 1s,
      bottom 1s;
  }

  img:nth-child(1) {
    bottom: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }

  img:nth-child(2) {
    top: 50%;
    transform: translate(-50%, -50%);
  }

  img:nth-child(3) {
    top: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }

  &.visible img:nth-child(1) {
    bottom: 65%;
  }

  &.visible img:nth-child(3) {
    top: 65%;
  }

  div {
    position: relative;
    z-index: 1;
    padding-top: 1rem;
    padding-right: clamp(7.25rem, 42vw, 12.5rem);
    width: 100%;
    max-width: none;
    opacity: 0;
    transition: opacity 0.75s;
    margin-left: 0.75rem;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      width: 0;
      opacity: 0;
      height: 1px;
      background-color: var(--text-inverse-primary);
      transition:
        opacity 0.75s,
        width 1s;
    }

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      transform: translateY(-2px);
      opacity: 0;
      height: 5px;
      width: 5px;
      border-radius: 100%;
      background-color: var(--text-inverse-primary);
      transition: opacity 0.25s 0.75s;
    }
  }

  div:nth-of-type(1) {
    margin-bottom: 2.25rem;

    &::before {
      left: 0;
    }
  }

  div:nth-of-type(2) {
    margin-bottom: 0;

    &::before {
      left: 0;
    }
  }

  &.visible {
    div::before {
      width: min(12.25rem, 100%);
      opacity: 1;
    }

    div::after {
      left: min(12.25rem, calc(100% - 5px));
      opacity: 1;
    }

    div:nth-of-type(1),
    div:nth-of-type(2) {
      opacity: 1;
    }
  }

  @media ${mqTabletMin} {
    height: 25rem;
    min-height: unset;
    width: 3.625rem;
    margin-left: auto;
    margin-right: auto;
    margin-top: 0;
    display: block;

    > span {
      position: static;
      z-index: auto;
      top: auto;
      right: auto;
      bottom: auto;
      display: unset;
      align-items: unset;
      justify-content: unset;
      width: auto;
      max-width: unset;
      pointer-events: auto;
    }

    video {
      position: absolute;
      height: 25rem;
      top: 50%;
      left: 20%;
      transform: translate(-50%, -50%);
      max-height: none;
      width: auto;
      max-width: none;
      object-fit: unset;
    }

    img {
      position: absolute;
      grid-area: unset;
      align-self: unset;
      justify-self: unset;
      max-width: 3.625rem;
      right: unset;
      left: 50%;
    }

    img:nth-child(1) {
      bottom: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }

    img:nth-child(2) {
      top: 50%;
      transform: translate(-50%, -50%);
    }

    img:nth-child(3) {
      top: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }

    div {
      position: absolute;
      z-index: auto;
      width: 11.5rem;
      max-width: none;
      padding-right: 0;
      margin-bottom: 0;
      transition:
        top 1s,
        opacity 0.75s;
    }

    div:nth-of-type(1) {
      top: 50%;
      right: 5.625rem;
    }

    div:nth-of-type(2) {
      top: 50%;
      right: unset;
      left: 5.625rem;
    }

    &.visible {
      img:nth-child(1) {
        bottom: 65%;
      }

      img:nth-child(3) {
        top: 65%;
      }

      div::before {
        width: 12.25rem;
        opacity: 1;
      }

      div::after {
        left: 12.25rem;
        opacity: 1;
      }

      div:nth-of-type(1) {
        top: 50%;
        opacity: 1;
      }

      div:nth-of-type(2) {
        top: 30%;
        opacity: 1;

        &::before {
          left: unset;
          right: 0rem;
        }

        /* Dot sits at the line's pill-side (left) end. */
        &::after {
          left: calc(100% - 12.25rem - 5px);
        }
      }
    }
  }

  @media (${mqXLargeMin}) {
    margin: unset;
    position: absolute;
    top: 0;
    left: 66%;
    height: 100%;
  }
`;

const PillTitle = styled.p`
  margin-bottom: 0.5rem;
  font-size: 16px;
  font-style: normal;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.07px;

  @media ${mqLargeMin} {
    line-height: 120%;
  }
`;

const PillContent = styled.p`
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.07px;
`;

const BundleProductSwapContainer = styled.div`
  margin-top: 4rem;
  border-radius: 100px;
  padding: 0.75rem;
  display: flex;
  gap: 0.5rem;
  width: fit-content;
  background: linear-gradient(
      0deg,
      rgba(226, 226, 226, 0.5) 0%,
      rgba(226, 226, 226, 0.5) 100%
    ),
    var(--scrim-glass-dark-08, rgba(87, 94, 85, 0.08));
`;

const BundleProductSwap = styled.button`
  ${BodySmReg};
  position: relative;
  appearance: none;
  border: 0;
  border-radius: 100px;
  padding: 0.625rem 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--fill-brand);
  cursor: pointer;
  transition: background 0.2s;
  overflow: hidden;

  &.selected {
    background: var(--fill-primary);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 3.75rem;
      background: rgba(0, 0, 0, 0.06);
    }
  }
`;

const BundleProductSwapSwatch = styled.span`
  border-radius: 100%;
  height: 1.25rem;
  width: 1.25rem;
  display: inline-block;
`;

const OpenTrialsButton = styled.button`
  appearance: none;
  display: inline-flex;
  margin-top: 1rem;
  border: 0;
  align-items: center;
  gap: 0.5rem;
  text-decoration: underline;
  font-weight: 500;
  color: var(--fill-bright);
  background-color: transparent;
  cursor: pointer;
`;

type EssentialsNutrientsDeliveryProps = {
  productInfo: unknown;
  essentials: EssentialsModule;
  bundleItems?: EssentialsBundleItem[];
  isDSVariant?: boolean;
};

const EssentialsNutrientsDelivery = ({
  productInfo,
  essentials,
  bundleItems,
  isDSVariant = false,
}: EssentialsNutrientsDeliveryProps) => {
  if (!essentials) {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [bundleEssentialsToDisplay, setBundleEssentialsToDisplay] = useState(0);

  const bundleItem = bundleItems?.[bundleEssentialsToDisplay];
  const delivery = bundleItem ? bundleItem.delivery : essentials.delivery;
  const deliveryBackground = delivery?.backgroundColor;
  const deliveryTitle = delivery?.title;
  const deliveryContent = delivery?.content;
  const deliveryPillImages = delivery?.pillImages ?? [];
  const deliveryPills = delivery?.pills ?? [];
  // The pill video is always the PDP's own, even while a bundle item is selected.
  const deliveryPillVideo = essentials.delivery?.pillVideo;
  const deliveryPillVideoURL = delivery?.pillVideoUrl;

  const [pillRef, pillInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
    onChange: (inView, entry) => {
      entry.target.style.opacity = inView ? 1 : 0;
    },
  });

  if (!deliveryTitle) {
    return null;
  }

  return (
    <NutrientDelivery
      style={{ background: deliveryBackground }}
      className={`${isDSVariant ? 'is-ds01' : ''}`}
    >
      <NutrientDeliveryTitle
        dangerouslySetInnerHTML={{
          __html: deliveryTitle.replace('®', '<sup>®</sup>'),
        }}
      />
      <NutrientDeliveryContent>{deliveryContent}</NutrientDeliveryContent>
      {isDSVariant && (
        <>
          <OpenTrialsButton onClick={() => setIsOpen(true)}>
            Learn More
            <Icon name='arrow-right' size='md' />
          </OpenTrialsButton>
          <ClinicalTrials
            productInfo={productInfo}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          >
            <DSLearnMore imageUrl={essentials.learnMoreImageUrl} />
          </ClinicalTrials>
        </>
      )}
      {bundleItems && (
        <BundleProductSwapContainer>
          {bundleItems.map((item, bundleModuleIndex) => (
            <BundleProductSwap
              key={`bundle-essentials-${item.key}`}
              className={
                bundleEssentialsToDisplay === bundleModuleIndex
                  ? 'selected'
                  : ''
              }
              onClick={() => setBundleEssentialsToDisplay(bundleModuleIndex)}
            >
              <BundleProductSwapSwatch
                style={{
                  backgroundColor: item.bundleSwatchColor,
                }}
              ></BundleProductSwapSwatch>
              {item.bundleLabel}
            </BundleProductSwap>
          ))}
        </BundleProductSwapContainer>
      )}
      <DeliveryPillParts
        className={`${pillInView ? 'visible' : ''}`}
        ref={pillRef}
      >
        {(deliveryPillVideo?.sources?.[0]?.url || deliveryPillVideoURL) && (
          <RawHtmlVideo
            src={deliveryPillVideoURL}
            backupSrc={deliveryPillVideo?.sources?.[0]?.url}
          />
        )}
        {!deliveryPillVideo?.sources?.[0]?.url &&
          !deliveryPillVideoURL &&
          deliveryPillImages.length > 0 &&
          deliveryPillImages.map((media, deliveryPillIndex) => {
            if (!media?.url) {
              return null;
            }
            return (
              <img
                key={`pill-image-${deliveryPillIndex}`}
                src={media.url}
                alt={media.alt || ''}
              />
            );
          })}
        {deliveryPills.length > 0 &&
          deliveryPills.map((pill, contentIndex) => (
            <div key={`pill-content-${contentIndex}`}>
              <PillTitle>{pill.title}</PillTitle>
              <PillContent>{pill.content}</PillContent>
            </div>
          ))}
      </DeliveryPillParts>
    </NutrientDelivery>
  );
};

export default EssentialsNutrientsDelivery;
